import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Plus, Edit3, Trash2, ExternalLink, Upload, Loader2, X, Search, Download } from "lucide-react";

const STATUS_STYLES = {
  published: { bg: "bg-champagne/15", text: "text-aubergine", dot: "bg-champagne", label: "Published" },
  draft: { bg: "bg-aubergine/10", text: "text-aubergine/65", dot: "bg-aubergine/40", label: "Draft" },
};

const DOC_BADGES = {
  new: "New",
  rewritten: "Rewritten",
  refreshed: "Refreshed",
};

export default function AdminDocs() {
  const [sections, setSections] = useState([]);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  // Import dialog state
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importReplace, setImportReplace] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importMessage, setImportMessage] = useState("");
  const fileRef = useRef(null);

  // Backup export state
  const [exporting, setExporting] = useState(false);

  const onExportBackup = async () => {
    setExporting(true);
    try {
      const r = await api.get("/admin/docs/export.zip", { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const stamp = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, "").slice(0, 12);
      const link = document.createElement("a");
      link.href = url;
      link.download = `trex-docs-backup-${stamp}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Backup export failed");
    } finally {
      setExporting(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/docs");
      setSections(r.data.sections || []);
      setPages(r.data.pages || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const onDelete = async (pageId) => {
    if (!window.confirm("Delete this docs page? This cannot be undone.")) return;
    await api.delete(`/admin/docs/pages/${pageId}`);
    load();
  };

  const togglePublish = async (page) => {
    const next = { ...page, is_published: !page.is_published };
    delete next.created_at;
    delete next.updated_at;
    delete next.page_id;
    try {
      await api.put(`/admin/docs/pages/${page.page_id}`, next);
      load();
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Failed to update page");
    }
  };

  const onImport = async () => {
    if (!importFile) return;
    setImporting(true);
    setImportMessage("");
    const fd = new FormData();
    fd.append("file", importFile);
    fd.append("replace", importReplace ? "true" : "false");
    try {
      const r = await api.post("/admin/docs/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportMessage(`Imported ${r.data.pages_upserted} pages across ${r.data.sections_upserted} sections${r.data.replaced ? " (replaced existing)" : ""}.`);
      setImportFile(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) {
      setImportMessage(e?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const filtered = pages.filter((p) =>
    !q ||
    p.title.toLowerCase().includes(q.toLowerCase()) ||
    p.slug.toLowerCase().includes(q.toLowerCase()) ||
    (p.section_slug || "").toLowerCase().includes(q.toLowerCase())
  );

  // Group filtered pages by section, preserving section order
  const sectionMap = new Map(sections.map((s) => [s.slug, s]));
  const grouped = sections.map((s) => ({
    section: s,
    items: filtered.filter((p) => p.section_slug === s.slug).sort((a, b) => (a.order ?? 99) - (b.order ?? 99)),
  })).filter((g) => g.items.length > 0);

  const formatDate = (d) => {
    if (!d) return "—";
    try { return new Date(d).toLocaleDateString(); } catch { return "—"; }
  };

  return (
    <div data-testid="admin-docs-page">
      <header className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-champagne mb-2">Content</p>
          <h1 className="font-serif text-3xl text-aubergine">Docs</h1>
          <p className="mt-2 text-sm text-aubergine/65">Manage the public documentation hub at /docs.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={onExportBackup}
            disabled={exporting}
            data-testid="admin-docs-backup-button"
            className="inline-flex items-center gap-2 rounded-md border border-aubergine/20 hover:bg-aubergine/5 px-4 py-2.5 font-mono text-xs tracking-[0.04em] disabled:opacity-50 disabled:cursor-not-allowed"
            title="Download every docs page as a .docx bundled in a zip"
          >
            {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
            {exporting ? "Preparing…" : "Download backup"}
          </button>
          <button
            onClick={() => { setImportOpen(true); setImportMessage(""); }}
            data-testid="admin-docs-import-button"
            className="inline-flex items-center gap-2 rounded-md border border-aubergine/20 hover:bg-aubergine/5 px-4 py-2.5 font-mono text-xs tracking-[0.04em]"
          >
            <Upload className="h-3.5 w-3.5" /> Import package
          </button>
          <Link
            to="/admin/docs/new"
            data-testid="admin-docs-new-button"
            className="inline-flex items-center gap-2 rounded-md bg-aubergine text-bone hover:bg-aubergine/90 px-4 py-2.5 font-mono text-xs tracking-[0.04em]"
          >
            <Plus className="h-3.5 w-3.5" /> New page
          </Link>
        </div>
      </header>

      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-aubergine/40" />
          <input
            data-testid="admin-docs-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search docs…"
            className="w-full rounded-md border border-aubergine/15 bg-white pl-9 pr-3 py-2 text-sm outline-none focus:border-champagne"
          />
        </div>
        <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-aubergine/55">
          {sections.length} sections · {pages.length} pages
        </p>
      </div>

      {loading ? (
        <p className="text-aubergine/55 text-sm">Loading…</p>
      ) : grouped.length === 0 ? (
        <div className="rounded-lg border border-aubergine/10 bg-white p-12 text-center" data-testid="admin-docs-empty">
          <p className="text-sm text-aubergine/65">No docs pages yet.</p>
          <p className="mt-2 text-xs text-aubergine/55">
            <button onClick={() => setImportOpen(true)} className="text-champagne hover:underline">Import a docs package</button>
            {" "}or{" "}
            <Link to="/admin/docs/new" className="text-champagne hover:underline">create the first page →</Link>
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {grouped.map(({ section, items }) => (
            <div key={section.slug} className="rounded-lg border border-aubergine/10 bg-white overflow-hidden" data-testid={`admin-docs-section-${section.slug}`}>
              <div className="px-4 py-3 border-b border-aubergine/10 bg-bone/60 flex items-center justify-between">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-aubergine/45">Section</p>
                  <h2 className="text-sm font-medium text-aubergine">{section.title}</h2>
                </div>
                <p className="font-mono text-[10px] text-aubergine/55">{items.length} page{items.length !== 1 ? "s" : ""}</p>
              </div>
              <table className="w-full text-sm">
                <thead className="text-left text-[11px] font-mono uppercase tracking-[0.12em] text-aubergine/55">
                  <tr>
                    <th className="px-4 py-3">Title</th>
                    <th className="px-4 py-3 hidden md:table-cell">Slug</th>
                    <th className="px-4 py-3 hidden md:table-cell">Badge</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 hidden lg:table-cell">Updated</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((p) => {
                    const status = p.is_published ? STATUS_STYLES.published : STATUS_STYLES.draft;
                    return (
                      <tr key={p.page_id} className="border-t border-aubergine/10 hover:bg-bone/40 transition-colors">
                        <td className="px-4 py-3">
                          <Link to={`/admin/docs/${p.page_id}`} className="font-medium text-aubergine hover:text-champagne" data-testid={`admin-doc-row-${p.page_id}`}>{p.title}</Link>
                          {p.description && <div className="text-xs text-aubergine/55 truncate max-w-md">{p.description}</div>}
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <span className="font-mono text-[10px] text-aubergine/55">/{p.section_slug}/{p.slug}</span>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          {DOC_BADGES[p.status] ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-champagne/12 text-champagne px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider">
                              {DOC_BADGES[p.status]}
                            </span>
                          ) : (
                            <span className="font-mono text-[10px] text-aubergine/35">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => togglePublish(p)}
                            data-testid={`admin-doc-toggle-publish-${p.page_id}`}
                            title={p.is_published ? "Click to unpublish" : "Click to publish"}
                            className={`inline-flex items-center gap-1.5 rounded-full ${status.bg} ${status.text} px-2 py-0.5 text-[11px] font-mono uppercase tracking-wider hover:opacity-80 transition-opacity`}
                          >
                            <span className={`h-1.5 w-1.5 rounded-full ${status.dot}`} />
                            {status.label}
                          </button>
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell text-aubergine/65 text-xs">{formatDate(p.updated_at)}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="inline-flex items-center gap-2">
                            {p.is_published && (
                              <a href={`/docs/${p.section_slug}/${p.slug}`} target="_blank" rel="noreferrer" data-testid={`admin-doc-view-${p.page_id}`} className="text-aubergine/55 hover:text-champagne" title="View public">
                                <ExternalLink className="h-3.5 w-3.5" />
                              </a>
                            )}
                            <Link to={`/admin/docs/${p.page_id}`} data-testid={`admin-doc-edit-${p.page_id}`} className="text-aubergine/55 hover:text-champagne" title="Edit">
                              <Edit3 className="h-3.5 w-3.5" />
                            </Link>
                            <button onClick={() => onDelete(p.page_id)} data-testid={`admin-doc-delete-${p.page_id}`} className="text-aubergine/55 hover:text-signal-red" title="Delete">
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {/* Sections without any matching pages */}
      {!loading && q === "" && (
        <div className="mt-6 text-xs text-aubergine/45">
          {sections.filter((s) => !pages.some((p) => p.section_slug === s.slug)).map((s) => (
            <p key={s.slug}>Empty section: <span className="font-mono">{s.slug}</span></p>
          ))}
        </div>
      )}

      {/* Sections without any matching pages (when not filtering) — referenced by sectionMap to silence eslint */}
      <span className="hidden">{sectionMap.size}</span>

      {/* Import dialog */}
      {importOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-aubergine/40 backdrop-blur-sm" onMouseDown={() => !importing && setImportOpen(false)}>
          <div onMouseDown={(e) => e.stopPropagation()} className="w-full max-w-lg rounded-lg border border-aubergine/10 bg-white shadow-xl overflow-hidden" data-testid="admin-docs-import-dialog">
            <div className="px-5 py-4 border-b border-aubergine/10 flex items-center justify-between">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-aubergine/55">Docs</p>
                <h2 className="text-base font-medium text-aubergine">Import documentation package</h2>
              </div>
              <button onClick={() => !importing && setImportOpen(false)} data-testid="admin-docs-import-close" className="text-aubergine/45 hover:text-aubergine"><X className="h-4 w-4" /></button>
            </div>
            <div className="px-5 py-5 space-y-4">
              <p className="text-xs text-aubergine/65 leading-relaxed">
                Upload a <span className="font-mono">.zip</span> with a <span className="font-mono">manifest.json</span> + markdown files (same format as the seed package). Existing pages with matching section/slug are updated in place.
              </p>
              <div>
                <label className="block text-xs font-medium text-aubergine mb-1.5">Package (.zip)</label>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".zip"
                  onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                  data-testid="admin-docs-import-file"
                  className="block w-full text-xs file:mr-3 file:rounded-md file:border-0 file:bg-aubergine file:text-bone file:px-3 file:py-2 file:font-mono file:text-[11px] file:tracking-wider file:hover:bg-aubergine/90 cursor-pointer"
                />
              </div>
              <label className="flex items-start gap-2 text-xs text-aubergine/75 cursor-pointer">
                <input
                  type="checkbox"
                  checked={importReplace}
                  onChange={(e) => setImportReplace(e.target.checked)}
                  data-testid="admin-docs-import-replace"
                  className="mt-0.5"
                />
                <span>
                  <span className="font-medium text-aubergine">Replace all existing docs</span>
                  <span className="block text-aubergine/55">Wipes every section and page before importing. Cannot be undone.</span>
                </span>
              </label>
              {importMessage && (
                <div className={`rounded-md px-3 py-2 text-xs ${importMessage.toLowerCase().includes("fail") || importMessage.toLowerCase().includes("error") ? "bg-signal-red/10 text-signal-red border border-signal-red/30" : "bg-champagne/15 text-aubergine border border-champagne/30"}`} data-testid="admin-docs-import-message">
                  {importMessage}
                </div>
              )}
            </div>
            <div className="px-5 py-4 border-t border-aubergine/10 flex items-center justify-end gap-2 bg-bone/30">
              <button onClick={() => !importing && setImportOpen(false)} data-testid="admin-docs-import-cancel" className="rounded-md border border-aubergine/20 hover:bg-aubergine/5 px-4 py-2 font-mono text-xs">Cancel</button>
              <button onClick={onImport} disabled={!importFile || importing} data-testid="admin-docs-import-submit" className="inline-flex items-center gap-2 rounded-md bg-aubergine text-bone hover:bg-aubergine/90 px-4 py-2 font-mono text-xs disabled:opacity-50">
                {importing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                {importing ? "Importing…" : "Import"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
